import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Platform } from 'react-native';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import {
    BUTTON_RENT,
    DETAIL_ERROR_CANCEL,
    DETAIL_ERROR_CODE,
    DETAIL_ERROR_DESCRIPTION,
    DETAIL_ERROR_TITLE,
    DETAIL_WATCH_BUTTON,
    RENT_OVERLAY_TITLE,
} from 'config/testID';

import { Bookmark } from 'store/bookmarks/reducer.types';
import { selectAppState, selectLayoutConfig } from 'store/config/selectors';
import { ProductType, PurchaseProduct } from 'store/railsBuilder/reducer.types';
import { selectPurchaseCapability } from 'store/user/selectors';
import { isCapabilityValid } from 'store/utils';

import { VodEditorial } from 'services/data-manager/types';

import { isTabletOrWeb, isWeb } from 'utils/responsiveness';

import styles from 'screens/Detail/components/DetailPageButtons/styles';
import DetailPageDropdownButton from 'screens/Detail/components/DetailPageDropdownButton';
import DetailPageSubscribeButton from 'screens/Detail/components/DetailPageSubscribeButton/DetailPageSubscribeButton';
import DetailPurchaseDescription from 'screens/Detail/components/DetailPurchaseDescription';
import useAssetTransactions from 'screens/Detail/hooks/useAssetTransactions';
import useModalHandler from 'screens/Detail/hooks/useModalHandler';
import useRent from 'screens/Detail/hooks/useRent';
import useVodHighUsageRestriction from 'screens/Detail/hooks/useVodHighUsageRestriction';
import {
    CLOSE_BUTTON,
    OK_BUTTON,
    RENT_BUTTON,
    RENT_ERROR_DESCRIPTION,
    RENT_ERROR_TITLE,
    RENT_OVERLAY_DESCRIPTION,
    TVOD_RENT_GO_TO_WEB_BODY,
    TVOD_RENT_GO_TO_WEB_TITLE,
} from 'screens/Detail/languageConst';
import useParentalControl from 'screens/PlayerModal/hooks/useParentalControl';

import ActionButton from 'components/CustomButtons/ActionButton';
import ProgressButton from 'components/CustomButtons/ProgressButton';
import DialogView from 'components/Dialog';
import dialogStyles from 'components/Dialog/style';
import ErrorDialog from 'components/ErrorDialog';
import GenericPinDialog from 'components/GenericPinDialog';

import DetailPageSubscribeModal from '../DetailPageSubscribeModal/DetailPageSubscribeModal';

type Props = {
    item: VodEditorial;
    ableToPlayVod: boolean;
    primaryColor?: string;
    watchButtonLabel: string;
    bookmark: Bookmark | undefined;
    videoDuration: number;
    kidsProfileActive: boolean;
    onWatchPress(): void;
};

function DetailPageVodButtons({
    item,
    ableToPlayVod,
    primaryColor,
    watchButtonLabel,
    bookmark,
    videoDuration,
    kidsProfileActive,
    onWatchPress,
}: Props) {
    const { primary } = useSelector(selectLayoutConfig);
    const appState = useSelector(selectAppState);
    const purchaseCapability = useSelector(selectPurchaseCapability);
    const { t } = useTranslation();

    const { errorCode, rentAsset, errorStatus } = useRent();
    const { rentOptions, subscriptionOptions } = useAssetTransactions(
        item as VodEditorial,
    );
    const { showPinDialog, onShowPinDialog, onParentalCheckDismiss } =
        useParentalControl({
            fullscreen: false,
            onSetPlayerError: () => null,
            onResetPlayerError: () => null,
        });
    const VodHighUsageRestrictionErrorComponent =
        useVodHighUsageRestriction(rentOptions);

    const isSeries = useMemo(() => {
        return !!item.seriesRef;
    }, [item]);

    const isAssetSubscribable = useMemo(() => {
        return subscriptionOptions && subscriptionOptions.length > 0;
    }, [subscriptionOptions]);

    const isAssetRentable = useMemo(() => {
        return !kidsProfileActive && rentOptions && rentOptions.length > 0;
    }, [rentOptions]);

    const shouldDisplayDropdown = isAssetSubscribable && isAssetRentable;
    const shouldDisplaySecondaryButton =
        !shouldDisplayDropdown && (isAssetRentable || isAssetSubscribable);
    const shouldDisplayPurchaseButtons = isCapabilityValid(purchaseCapability);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<PurchaseProduct>();
    const [errorDismissed, setErrorDismissed] = useState(false);
    const [closeIosDropdown, setCloseIosDropdown] = useState(false);

    const setIosDropdown = useCallback((value: boolean) => {
        // On iOS we cannot have multiple modals unless they are nested
        // This state is for hiding the dropdown button and its modal while another is open
        if (Platform.OS === 'ios') {
            setCloseIosDropdown(value);
        }
    }, []);

    const { onOpenModal, onCloseModal } = useModalHandler({
        setModalOpen: setIsModalOpen,
        onOpenOperation: () => {
            setIosDropdown(true);
        },
        onCloseOperation: () => {
            setIosDropdown(false);
        },
        onWebCloseOperation: () => {
            setIosDropdown(false);
        },
    });

    const onOptionClick = useCallback(
        (product: PurchaseProduct) => {
            setSelectedProduct(product);
            onOpenModal();
        },
        [onOpenModal],
    );

    const handlePinControlChecked = useCallback(() => {
        setErrorDismissed(false);

        if (selectedProduct) {
            rentAsset(selectedProduct?.id);
        }

        setTimeout(() => {
            onParentalCheckDismiss();
            onCloseModal();
        }, 100);
    }, [selectedProduct, rentAsset, onParentalCheckDismiss, onCloseModal]);

    const handleErrorClose = useCallback(() => {
        setErrorDismissed(true);
        onParentalCheckDismiss();
    }, [onParentalCheckDismiss]);

    const Description = useCallback(() => {
        if (selectedProduct) {
            return (
                <DetailPurchaseDescription
                    description={RENT_OVERLAY_DESCRIPTION}
                    product={selectedProduct}
                />
            );
        }

        return null;
    }, [selectedProduct]);

    const PinOverlay = useCallback(() => {
        return (
            <GenericPinDialog
                appState={appState}
                visible={showPinDialog}
                onClose={onParentalCheckDismiss}
                onSuccess={handlePinControlChecked}
                wasFullScreen
                modal
            />
        );
    }, [
        appState,
        showPinDialog,
        onParentalCheckDismiss,
        handlePinControlChecked,
    ]);

    useEffect(() => {
        if (shouldDisplaySecondaryButton) {
            if (subscriptionOptions && subscriptionOptions?.length > 0) {
                setSelectedProduct(subscriptionOptions[0]);
            } else if (rentOptions && rentOptions?.length > 0) {
                setSelectedProduct(rentOptions[0]);
            }
        }
    }, [
        shouldDisplaySecondaryButton,
        isAssetSubscribable,
        isAssetRentable,
        subscriptionOptions,
        rentOptions,
    ]);

    if (VodHighUsageRestrictionErrorComponent) {
        return VodHighUsageRestrictionErrorComponent;
    }

    if ((!!errorCode || errorStatus) && !errorDismissed) {
        return (
            <ErrorDialog
                style={[styles.errorDialog, dialogStyles.nonTransparentDialog]}
                title={RENT_ERROR_TITLE}
                description={RENT_ERROR_DESCRIPTION}
                actionButtonTitle={OK_BUTTON}
                actionButtonFillColor={primary}
                actionButtonTextStyle={styles.errorButtonText}
                actionButtonOnPress={handleErrorClose}
                errorCode={errorCode}
                titleTestID={DETAIL_ERROR_TITLE}
                descriptionTestID={DETAIL_ERROR_DESCRIPTION}
                actionButtonTestID={DETAIL_ERROR_CANCEL}
                errorCodeTestID={DETAIL_ERROR_CODE}
                errorStatus={errorStatus}
            />
        );
    }

    return (
        <>
            {isModalOpen &&
                selectedProduct &&
                (selectedProduct.type === ProductType.TRANSACTION ? (
                    <>
                        <DialogView
                            style={dialogStyles.nonTransparentDialog}
                            title={
                                isWeb
                                    ? item.title || ''
                                    : t(TVOD_RENT_GO_TO_WEB_TITLE)
                            }
                            description={
                                isWeb ? undefined : t(TVOD_RENT_GO_TO_WEB_BODY)
                            }
                            DescriptionComponent={
                                isWeb ? Description : undefined
                            }
                            actionButtonTitle={
                                isWeb ? RENT_BUTTON : CLOSE_BUTTON
                            }
                            actionButtonFillColor={primaryColor}
                            actionButtonTextStyle={styles.buttonText}
                            actionButtonOnPress={
                                isWeb ? onShowPinDialog : onCloseModal
                            }
                            cancelButtonTitle={isWeb ? CLOSE_BUTTON : undefined}
                            cancelButtonBordered
                            cancelButtonOnPress={
                                isWeb ? onCloseModal : undefined
                            }
                            titleTestID={RENT_OVERLAY_TITLE}
                            actionButtonTestID={isWeb ? BUTTON_RENT : undefined}
                            closeButtonTestID={isWeb ? CLOSE_BUTTON : undefined}
                            CloseComponent={PinOverlay}
                            descriptionNumberOfLines={0}
                            visible
                        />
                    </>
                ) : (
                    <DetailPageSubscribeModal
                        isModalOpen={isModalOpen}
                        onCloseModal={onCloseModal}
                    />
                ))}

            {ableToPlayVod && (
                <ProgressButton
                    style={[
                        styles.button,
                        styles.watchButton,
                        shouldDisplaySecondaryButton &&
                            (isTabletOrWeb
                                ? styles.hasExtraButtonBigScreen
                                : styles.hasExtraButtonPhone),
                        isTabletOrWeb && !isSeries && styles.buttonNonScaling,
                    ]}
                    buttonMinWidth={styles.watchButton.minWidth}
                    label={watchButtonLabel}
                    progress={bookmark?.position}
                    duration={videoDuration}
                    onPress={onWatchPress}
                    testID={DETAIL_WATCH_BUTTON}
                />
            )}

            {shouldDisplayPurchaseButtons && (
                <>
                    {shouldDisplayDropdown && !closeIosDropdown && (
                        <DetailPageDropdownButton
                            fillColor={primary}
                            onSelectItem={(selectItem) =>
                                onOptionClick(selectItem as PurchaseProduct)
                            }
                            rentOptions={rentOptions}
                            subscriptionOptions={subscriptionOptions}
                        />
                    )}

                    {shouldDisplaySecondaryButton &&
                        (isAssetRentable ? (
                            <ActionButton
                                numberOfLines={1}
                                buttonStyle={[
                                    styles.button,
                                    styles.watchButton,
                                    ableToPlayVod && styles.borderedButton,
                                    isTabletOrWeb && styles.buttonNonScaling,
                                ]}
                                buttonTextStyle={
                                    ableToPlayVod
                                        ? styles.transparentButtonText
                                        : styles.buttonText
                                }
                                title={BUTTON_RENT}
                                fillColor={
                                    ableToPlayVod ? 'transparent' : primaryColor
                                }
                                onPress={onOpenModal}
                                testID={BUTTON_RENT}
                            />
                        ) : (
                            <DetailPageSubscribeButton
                                isModalOpen={isModalOpen}
                                onCloseModal={onCloseModal}
                                onOpenModal={onOpenModal}
                                ableToPlayVod={ableToPlayVod}
                            />
                        ))}
                </>
            )}
        </>
    );
}

export default DetailPageVodButtons;
